import React, {useEffect, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import createAxiosInstance from '../support/AxiosSupport';
import {ArrowRightIcon, CalendarIcon, ClockIcon, MapPinIcon} from './icons';
import {
    eventDayLabel,
    eventTimeLabel,
    eventTypeLabel,
    formalityBadgeClass,
    formalityLabel,
} from '../support/calendarLabels';

/**
 * Блок «Сегодня и скоро» на главной (CTHS-175).
 *
 * <p>Делает интеграцию календаря видимой в том сценарии, ради которого она существует:
 * утром человек смотрит главную и должен сразу видеть, подо что сегодня одеваться. Под каждым
 * событием — действие «Подобрать образ», которое уносит контекст этого события в подбор.</p>
 *
 * <p><b>Когда блока нет.</b> Календарь не подключён или владелец выключил показ на главной —
 * блок не рендерится вовсе (FR-27). Приглашение подключить живёт в одном месте — Privacy
 * Center; дублировать его на главной значит превращать рабочий экран в рекламный щит.
 * Ошибка загрузки тоже прячет блок: календарь — вспомогательный сигнал, он не имеет права
 * занимать место сообщением о собственной неисправности.</p>
 */

interface EventContext {
    eventId: string;
    title: string | null;
    type: string;
    formality: string;
    outdoorProbability: number;
    online: boolean;
    durationHours: number | null;
    startTime: string;
    endTime: string | null;
    allDay: boolean;
    location: string | null;
}

interface CalendarHome {
    connected: boolean;
    showOnHome: boolean;
    events: EventContext[];
}

const TodayEventsBlock: React.FC = () => {
    const navigate = useNavigate();
    const [data, setData] = useState<CalendarHome | null>(null);
    const [loading, setLoading] = useState(true);
    const [failed, setFailed] = useState(false);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const api = await createAxiosInstance();
                const res = await api.get<CalendarHome>('/calendar/events/home', {params: {days: 3}});
                if (!cancelled) setData(res.data);
            } catch {
                if (!cancelled) setFailed(true);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    if (loading) {
        return (
            <section className="card p-6" data-testid="today-events-loading">
                <div className="h-5 w-40 rounded bg-gray-100 dark:bg-ink-700 animate-pulse"/>
                <div className="mt-4 space-y-3">
                    <div className="h-16 rounded-xl bg-gray-100 dark:bg-ink-700 animate-pulse"/>
                    <div className="h-16 rounded-xl bg-gray-100 dark:bg-ink-700 animate-pulse"/>
                </div>
            </section>
        );
    }

    if (failed || !data || !data.connected || !data.showOnHome) {
        return null;
    }

    return (
        <section className="card p-6" data-testid="today-events">
            <div className="flex items-center gap-2 mb-4">
                <CalendarIcon size={20} className="text-gray-500 dark:text-gray-400"/>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Сегодня и скоро</h2>
            </div>

            {data.events.length === 0 ? (
                <p className="text-gray-500 dark:text-gray-400" data-testid="today-events-empty">
                    На ближайшие дни событий нет — одевайтесь свободно.
                </p>
            ) : (
                <ul className="space-y-3" data-testid="today-events-list">
                    {data.events.map(event => (
                        <li
                            key={event.eventId}
                            className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between
                                rounded-xl border border-gray-100 dark:border-white/5 p-3"
                            data-testid={`today-event-${event.eventId}`}
                        >
                            <div className="min-w-0">
                                <div className="flex flex-wrap items-center gap-2">
                                    <span className="text-xs text-gray-500 dark:text-gray-400">
                                        {eventDayLabel(event.startTime)}
                                    </span>
                                    <span className="inline-flex items-center gap-1 text-xs text-gray-500
                                        dark:text-gray-400">
                                        <ClockIcon size={14}/>
                                        {eventTimeLabel(event.startTime, event.endTime, event.allDay)}
                                    </span>
                                    <span className={`badge ${formalityBadgeClass(event.formality)}`}>
                                        {formalityLabel(event.formality)}
                                    </span>
                                </div>
                                <p className="mt-1 text-gray-900 dark:text-gray-100 truncate">
                                    {event.title?.trim() || 'Событие без названия'}
                                </p>
                                <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
                                    {eventTypeLabel(event.type)}
                                    {event.online && ' · онлайн'}
                                    {event.location && (
                                        <span className="inline-flex items-center gap-1 ml-1">
                                            <MapPinIcon size={14}/>{event.location}
                                        </span>
                                    )}
                                </p>
                            </div>
                            <button
                                type="button"
                                onClick={() => navigate(`/outfit?eventId=${encodeURIComponent(event.eventId)}`)}
                                className="btn-secondary min-h-[44px] shrink-0 self-start sm:self-auto"
                                data-testid={`today-event-pick-${event.eventId}`}
                            >
                                Подобрать образ
                                <ArrowRightIcon size={16}/>
                            </button>
                        </li>
                    ))}
                </ul>
            )}
        </section>
    );
};

export default TodayEventsBlock;
